import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
    Box,
    Typography,
    Tabs,
    Tab,
    TextField,
    Checkbox,
    FormControlLabel,
    Paper,
    Button,
    Alert,
    useTheme,
    Link
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

import API from "../api/axiosInstance";
import TableSelection from "../components/reports/TableSelection";
import ReportLayout from "../components/reports/ReportLayout";
import ReportSchedule from "../components/reports/ReportSchedule";
import SaveStatusDialog from "../components/SaveStatusDialog";

import "../styles/ReportEditor.css";
import { ReportData } from "../types/interfaces";

const ReportEditor: React.FC = () => {
    const { reportId } = useParams();
    const navigate = useNavigate();
    const theme = useTheme();

    const [step, setStep] = useState(1);
    const [errors, setErrors] = useState<string[]>([]);
    const [saveStatus, setSaveStatus] = useState<"saving" | "success" | "error" | null>(null);
    const [saveError, setSaveError] = useState<string | null>(null);

    const [reportData, setReportData] = useState<ReportData>({
        name: "",
        description: "",
        active: true,
        teams: [],
        tables: [],
        layout: "",
        schedule: []
    });

    useEffect(() => {
        if (reportId) fetchReportDetails(reportId);
    }, [reportId]);

    const fetchReportDetails = async (id: string) => {
        try {
            const response = await API.get(`/reports/${id}`);
            const mappedTables = (response.data?.tables || []).map((table: any, index: number) => ({
                ...table,
                id: table.id || `temp-${index}`,
                fields: table.fields || []
            }));
            setReportData({ ...response.data, tables: mappedTables });
        } catch (error) {
            console.error("Error loading report:", error);
        }
    };

    const validateReport = () => {
        const issues: string[] = [];

        if (!reportData.name.trim()) issues.push("Report name is required.");
        if (reportData.tables.length === 0) issues.push("At least one table must be selected.");
        if (reportData.tables.some(tbl => !tbl.fields || tbl.fields.length === 0))
            issues.push("Each table must have at least one field.");

        setErrors(issues);
        return issues.length === 0;
    };

    const handleSave = async () => {
        if (!validateReport()) return;
        setSaveStatus("saving");
        setSaveError(null);

        try {
            if (reportId) {
                await API.put(`/reports/${reportId}`, reportData);
            } else {
                await API.post("/reports", reportData);
            }
            setSaveStatus("success");
            setTimeout(() => {
                setSaveStatus(null);
                navigate("/reports");
            }, 1000);
        } catch (error: any) {
            console.error("Error saving report:", error);
            setSaveStatus("error");
            setSaveError(error?.response?.data?.message || "Failed to save report.");
        }
    };

    return (
<Box
    className="report-editor"
    sx={{
        backgroundColor: theme.palette.background.default,
        color: theme.palette.text.primary,
        padding: 4,
        minHeight: "100vh",
        mt: 6  
    }}
>

            {/* Validation errors */}
            {errors.length > 0 && (
                <Box className="error-panel" mb={3}>
                    <Alert severity="error">
                        <Typography variant="subtitle1" gutterBottom>Validation Errors:</Typography>
                        <ul style={{ margin: 0, paddingLeft: "1.2rem" }}>
                            {errors.map((err, index) => (
                                <li key={index}>{err}</li>
                            ))}
                        </ul>
                    </Alert>
                </Box>
            )}

            {/* Header */}
            <Box
                display="flex"
                alignItems={{ xs: "flex-start", sm: "center" }}
                justifyContent="space-between"
                flexDirection={{ xs: "column", sm: "row" }}
                gap={2}
                mb={3}
            >
                <Box>
                    <Typography variant="h5" fontWeight={600}>
                        {reportId ? "Edit Report" : "Create Report"}
                    </Typography>
                    <Link
                        component="button"
                        onClick={() => navigate("/reports")}
                        underline="hover"
                        color="primary"
                        sx={{ display: "flex", alignItems: "center", fontWeight: 500, mt: 1 }}
                    >
                        <ArrowBackIcon sx={{ fontSize: 18, mr: 0.5 }} />
                        Back to Reports
                    </Link>
                </Box>

                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSave}
                    sx={{ alignSelf: { xs: "stretch", sm: "center" } }}
                >
                    Save
                </Button>
            </Box>

            {/* Step Tabs */}
            <Paper
                elevation={1}
                sx={{
                    mb: 3,
                    px: 2,
                    py: 1,
                    borderRadius: 2,
                    backgroundColor: theme.palette.mode === 'dark' ? '#2c2c2c' : '#f0f0f0',
                    overflowX: "auto"
                }}
            >
<Tabs
    value={step - 1}
    onChange={(_, index) => setStep(index + 1)}
    variant="scrollable"
    scrollButtons="auto"
    sx={{
        '.MuiTab-root': {
            textTransform: "none",
            fontWeight: 500,
            borderRadius: 2,
            px: 2,
            mx: 0.5,
            minHeight: 40,
        },
        '.Mui-selected': {
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.getContrastText(theme.palette.primary.main),
        }
    }}
>

                    <Tab label="Basic Info" />
                    <Tab label="Tables" />
                    <Tab label="Layout" />
                    <Tab label="Schedule" />
                </Tabs>
            </Paper>

            {/* Step Panels */}
            <Paper elevation={2} className="content-panel" sx={{ p: 3 }}>
                {step === 1 && (
                    <>
                        <Typography variant="h6" gutterBottom>Step 1: Basic Information</Typography>

                        <TextField
                            label="Report Name"
                            fullWidth
                            value={reportData.name}
                            onChange={(e) => setReportData({ ...reportData, name: e.target.value })}
                            sx={{ mb: 2 }}
                        />

                        <TextField
                            label="Description"
                            fullWidth
                            multiline
                            minRows={3}
                            value={reportData.description}
                            onChange={(e) => setReportData({ ...reportData, description: e.target.value })}
                            sx={{ mb: 2 }}
                        />

                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={reportData.active}
                                    onChange={() =>
                                        setReportData({ ...reportData, active: !reportData.active })
                                    }
                                />
                            }
                            label="Active"
                        />

                        <Box mt={2}>
                            <Button variant="contained" onClick={() => setStep(2)}>
                                Next
                            </Button>
                        </Box>
                    </>
                )}

                {step === 2 && (
                    <TableSelection
                        reportData={reportData}
                        setReportData={setReportData}
                        onNext={() => setStep(3)}
                    />
                )}

                {step === 3 && (
                    <ReportLayout
                        reportData={reportData}
                        setReportData={setReportData}
                        onNext={() => setStep(4)}
                    />
                )}

                {step === 4 && (
                    <ReportSchedule
                        reportData={reportData}
                        setReportData={setReportData}
                        onSave={handleSave}
                    />
                )}
            </Paper>

            {/* Save Dialog */}
            <SaveStatusDialog
                open={!!saveStatus}
                status={saveStatus || "saving"}
                message={saveError || ""}
                onClose={() => setSaveStatus(null)}
            />
        </Box>
    );
};

export default ReportEditor;
